/* eslint-disable no-console */
import React from "react";
import { useNavigate } from "react-router-dom";
import cuid from "cuid";
import { every, flatMap, some } from "lodash";
import { CloseCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { Alert } from "../../atoms";
import { IHotelBookingPhysicalRoom } from "../../../common/types";
import { addBooking30512HotelToFirestore } from "../../../firestore/firestoreService";
import { CartHotelTotalBtn } from "../Cart/CartHotelTotalBtn";
import { calculateDiscountedAmount } from "../../../common/util/util";
import { HotelRoomCartItem } from "./components/HotelRoomCartItem";
import { HotelCartWrapper } from "./Hotel.style";

const partnerId = process.env.REACT_APP_SITE_ID || "";
const vivaSourceCode = process.env.REACT_APP_VIVA_HOTEL_SOURCE_CODE || "";
interface IProps {
  rooms: IHotelBookingPhysicalRoom[];
  removeRoom?: (id: string) => void;
  startDate: string;
  endDate?: string;
  hotelId: string;
  exclusiveOfferId: string;
  checkInCheckOut?: string;
  totalPrice: number;
  netPrice: number;
  sellPrice: number;
  isTabletOrMobile: boolean;
  onShowCart?: () => void;
  hotelName: string;
}

function createRoomArray(roomData: IHotelBookingPhysicalRoom) {
  const rooms: IHotelBookingPhysicalRoom[] = [];
  for (let i = 0; i < roomData.roomNumber; i++) {
    rooms.push({
      adultNumber: roomData.adultNumber,
      cancelationPolicy: roomData.cancelationPolicy,
      childNumber: roomData.childNumber,
      id: cuid(),
      infantNumber: roomData.infantNumber,
      mealPrice: roomData.mealPrice,
      mealType: roomData.mealType,
      refundableDate: roomData.refundableDate,
      roomId: roomData.roomId,
      roomPrice: roomData.roomPrice,
      totalPrice: +roomData.roomPrice + +roomData.mealPrice,
      sellPrice: +roomData.sellPrice + +roomData.mealPrice,
      netPrice: +roomData.netPrice + +roomData.mealPrice,
      roomNumber: 1,
      mainGuest: null,
      onRequest: roomData.onRequest,
      downPaymentAmount: roomData.downPaymentAmount,
      dayExtrasDescriptions: Array.isArray(roomData.dayExtrasDescriptions)
        ? roomData.dayExtrasDescriptions.join(", ")
        : roomData.dayExtrasDescriptions || "",
      fullPaymentDueDate: roomData.fullPaymentDueDate,
    });
  }

  return rooms;
}
export const HotelCart: React.FC<IProps> = ({
  rooms,
  removeRoom,
  startDate,
  endDate,
  hotelId,
  exclusiveOfferId,
  checkInCheckOut,
  totalPrice,
  netPrice,
  sellPrice,
  isTabletOrMobile,
  onShowCart,
  hotelName,
}) => {
  console.log("Rendering HotelCart with rooms:", rooms);
  const history = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const checkInCheckOutValue =
    startDate && endDate
      ? `${moment(startDate).format("D MMM YYYY")} - ${moment(endDate).format(
          "D MMM YYYY",
        )}`
      : checkInCheckOut || "";
  console.log("rooms in cart:", rooms);
  const handleBookNow = async () => {
    try {
      setLoading(true);
      setError("");
      if (!hotelId) {
        throw new Error("No Hotel");
      }
      if (!rooms || rooms.length === 0) {
        throw new Error("No rooms");
      }
      const checkIn = startDate || "";
      const checkOut = endDate || "";
      if (!checkIn || !checkOut) {
        throw new Error("Invalid Check In or Check Out Date");
      }
      const physicalRooms: any[] = rooms.map((room) => ({
        rooms: createRoomArray(room),
      }));
      const downPaymentAmount = Number(rooms[0]?.downPaymentAmount);
      const partialPaymentPercent =
        Number.isFinite(downPaymentAmount) &&
        downPaymentAmount >= 0 &&
        downPaymentAmount <= 100
          ? downPaymentAmount
          : 30;
      console.log("Physical rooms to book:", physicalRooms);
      const booking = await addBooking30512HotelToFirestore({
        hotelId,
        hotelName,
        rooms: flatMap(physicalRooms, "rooms"),
        exclusiveOfferId,
        checkIn,
        checkOut,
        totalPayment: totalPrice,
        netPrice,
        sellPrice,
        partialPayment: calculateDiscountedAmount(
          +totalPrice,
          100 - partialPaymentPercent,
        ),
        remainingAmount: calculateDiscountedAmount(
          +totalPrice,
          partialPaymentPercent,
        ),
        paymentStatus: "PENDING",
        status: "NEW",
        onRequest: some(rooms, { onRequest: true }),
        partnerId,
        customerId: "",
        totalPrice,
        vivaSourceCode,
        allowPartialPayment: every(rooms, { allowPartialPayment: true }),
        finalPaymentDate:
          physicalRooms?.[0]?.rooms?.[0]?.fullPaymentDueDate || "",
        refundableDate: physicalRooms?.[0]?.rooms?.[0]?.refundableDate || "",
        dayExtrasDescriptions:
          physicalRooms?.[0]?.rooms?.[0]?.dayExtrasDescriptions || [],
        cancellationPolicy:
          physicalRooms?.[0]?.rooms?.[0]?.cancelationPolicy || "",
      });
      setLoading(false);
      history(`/hotel-booking/${booking.id}`);
    } catch (err: any) {
      setLoading(false);
      setError(err.message || err);
    }

    // eslint-disable-next-line no-console
  };
  return (
    <HotelCartWrapper>
      <div className="HotelCart-title">
        Πληροφορίες κράτησης{" "}
        {isTabletOrMobile && (
          <div className="cart-close">
            <CloseCircleOutlined onClick={onShowCart} />
          </div>
        )}
      </div>
      <div className="HotelCart-body">
        {rooms.length === 0 && (
          <div className="HotelCart-noRoom">
            Δεν έχει γίνει καμία Επιλογή Δωματίων ακόμη
          </div>
        )}
        {error !== "" && <Alert message={error} type="error" />}
        {rooms.length > 0 &&
          rooms.map((room) => (
            <HotelRoomCartItem
              key={room.id}
              hotelId={hotelId || ""}
              room={room}
              removeRoom={removeRoom}
              hotelName={hotelName}
              checkInCheckOut={checkInCheckOutValue}
            />
          ))}
        {rooms.length > 0 && (
          <div className="cart-footer">
            <CartHotelTotalBtn
              submit={removeRoom && handleBookNow}
              loading={loading}
              finalPrice={totalPrice}
              checkInCheckOut={checkInCheckOutValue}
              onRequest={some(rooms, { onRequest: true })}
            />
          </div>
        )}
      </div>
    </HotelCartWrapper>
  );
};
